/**
 * Clara 360 - Email API Service
 * Wrapper for Clara Email API (Node.js service on port 5018)
 */

const EMAIL_API_URL = 'http://localhost:5018/api/email/send';

/**
 * Send email via Clara Email API
 * @param {Object} params
 * @param {string} params.to - Recipient email
 * @param {string} params.subject - Email subject
 * @param {string} params.text - Plain text body
 * @param {string} params.html - HTML body (optional, falls back to text)
 * @param {Object} params.context - Context for logging (rent_payment_id, tenant_id, etc.)
 * @returns {Promise<Object>} { success, messageId, duration }
 */
async function sendEmail({ to, subject, text, html, context = {} }) {
  try {
    console.log('[EMAIL-API] Sending email to:', to);
    
    // Prepare request
    const payload = {
      to,
      subject,
      message: html || text.replace(/\n/g, '<br>')
    };
    
    // Send via Clara Email API
    const response = await fetch(EMAIL_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Email API request failed');
    }
    
    const result = await response.json();
    
    console.log('[EMAIL-API] Email sent successfully:', result.messageId);
    
    return {
      success: result.success,
      messageId: result.messageId,
      duration: result.duration,
      context
    };
    
  } catch (error) {
    console.error('[EMAIL-API] Send error:', error);
    throw error;
  }
}

/**
 * Send email with PDF attachment
 * @param {Object} params
 * @param {string} params.to - Recipient email
 * @param {string} params.subject - Email subject
 * @param {string} params.text - Plain text body
 * @param {Object} params.attachment - { filename, content (base64), contentType }
 * @param {Object} params.context - Context for logging
 * @returns {Promise<Object>}
 */
async function sendEmailWithAttachment({ to, subject, text, attachment, context = {} }) {
  try {
    console.log('[EMAIL-API] Sending email with attachment to:', to);
    
    const payload = {
      to,
      subject,
      message: text.replace(/\n/g, '<br>'),
      attachment: {
        filename: attachment.filename || 'Dokument.pdf',
        content: attachment.content,
        contentType: attachment.contentType || 'application/pdf'
      }
    };
    
    const response = await fetch(EMAIL_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Email API request failed');
    }
    
    const result = await response.json();
    
    console.log('[EMAIL-API] Email with attachment sent:', result.messageId);
    
    return {
      success: result.success,
      messageId: result.messageId,
      duration: result.duration,
      context
    };
    
  } catch (error) {
    console.error('[EMAIL-API] Send with attachment error:', error);
    throw error;
  }
}

/**
 * Test email sending
 * @param {string} to - Test recipient
 * @returns {Promise<Object>}
 */
async function testEmail(to) {
  try {
    const response = await fetch('http://localhost:5018/api/email/test', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ to })
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Test email failed');
    }
    
    return await response.json();
  } catch (error) {
    console.error('[EMAIL-API] Test error:', error);
    throw error;
  }
}

/**
 * Check API health
 * @returns {Promise<Object>}
 */
async function checkHealth() {
  try {
    const response = await fetch('http://localhost:5018/api/email/health');
    
    if (!response.ok) {
      throw new Error('Health check failed');
    }
    
    return await response.json();
  } catch (error) {
    console.error('[EMAIL-API] Health check error:', error);
    return {
      status: 'error',
      message: error.message
    };
  }
}

export default {
  sendEmail,
  sendEmailWithAttachment,
  testEmail,
  checkHealth
};
